import type { CurrentSession } from '@nlc/contracts';
import { CloudUploadOutlined } from '@mui/icons-material';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

import { isInProgress, type QueuedUpload } from '../../api/upload-queue.js';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { EmptyState } from '../../components/states.js';
import { formatSize, phaseLabel, targetLabel } from './upload-labels.js';
import { useUploadQueue } from './use-upload-queue.js';
import { useUploadRetry } from './use-upload-retry.js';

/**
 * Top-bar badge with the member's pending and failed uploads, and a dialog to retry or discard
 * them. Mounting it also arms the automatic retry on sign-in and reconnect.
 */
export function UploadsIndicator({ session }: { session: CurrentSession }): React.JSX.Element {
  useUploadRetry(session);
  const uploads = useUploadQueue(session);
  const [open, setOpen] = useState(false);
  const [discarding, setDiscarding] = useState<QueuedUpload | null>(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const count = uploads.entries.length;

  return (
    <>
      <Tooltip title="Uploads">
        <IconButton
          aria-label={count === 0 ? 'Uploads' : `Uploads, ${count} waiting`}
          onClick={() => setOpen(true)}
          sx={{ minWidth: 48, minHeight: 48 }}
        >
          <Badge
            badgeContent={count}
            color={uploads.failed > 0 ? 'error' : 'primary'}
            invisible={count === 0}
          >
            <CloudUploadOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        aria-labelledby="uploads-dialog-title"
      >
        <DialogTitle id="uploads-dialog-title">Uploads</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Online-first: uploads need a connection. Anything that does not finish is kept on this
              device for 24 hours so you can retry it, and is cleared when you sign out.
            </Alert>
            {uploads.storage === 'memory' ? (
              <Alert severity="warning">
                This browser cannot keep uploads across a reload. Stay on the page until they
                finish.
              </Alert>
            ) : null}
            {count === 0 ? (
              <EmptyState
                title="Nothing waiting"
                detail="Photos and files you add upload right away. Anything that does not finish shows up here."
              />
            ) : (
              uploads.entries.map((entry) => {
                const running = isInProgress(entry);
                return (
                  <Card key={entry.id} variant="outlined">
                    <CardContent>
                      <Stack spacing={1.5}>
                        <div>
                          <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                            {entry.fileName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {targetLabel(entry.targetType)} · {formatSize(entry.blob.size)} · added{' '}
                            {new Date(entry.createdAt).toLocaleString()}
                          </Typography>
                        </div>
                        {running ? (
                          <div>
                            <LinearProgress aria-label="Upload progress" />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {phaseLabel(entry.phase)}…
                            </Typography>
                          </div>
                        ) : entry.phase === 'failed' ? (
                          <Alert severity="error">
                            {entry.lastError ?? 'The upload did not complete.'}
                            {entry.attempts > 1 ? ` (${entry.attempts} attempts)` : ''}
                          </Alert>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {phaseLabel(entry.phase)}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            disabled={running}
                            onClick={() => uploads.retry(entry.id)}
                            sx={{ minHeight: 48, flex: 1 }}
                          >
                            Retry
                          </Button>
                          <Button
                            color="error"
                            disabled={running}
                            onClick={() => setDiscarding(entry)}
                            sx={{ minHeight: 48, flex: 1 }}
                          >
                            Discard
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ minHeight: 48 }}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={uploads.retryable === 0}
            onClick={uploads.retryAll}
            sx={{ minHeight: 48 }}
          >
            Retry all
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={discarding !== null}
        title="Discard this upload?"
        description={
          discarding
            ? `${discarding.fileName} has not reached the server. Discarding removes it from this device; you would need to capture or choose it again.`
            : ''
        }
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          if (discarding) uploads.discard(discarding.id);
          setDiscarding(null);
        }}
        onCancel={() => setDiscarding(null)}
      />
    </>
  );
}
